import { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import { useQueryParam } from 'use-query-params'
import { Grid, Typography, makeStyles, Container, Button } from '@material-ui/core'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import { useSearchDeletedProject, DeletedProjectRequest, DeletedProject } from '../services/um/useSearchDeletedProject'
import { convertDateToFormatTH, convertDateToFormatEN } from '../utilities/date'
import useTranslate from '../i18n/useTranslate'
import DeletedProjectPanel from '../component/report/deleted-project/Panel'
import deletedProjectExport from '../component/report/deleted-project/report'
import useAuthenStore from '../stores/authen/authenStore'
import SearchAccordion from '../control/accordion/SearchAccordion'
import Table from '../component/report/deleted-project/Table'
import useLoading from '../control/loading/useLoading'

const DeletedProjectForm = () => {
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const classes = useStyles()
    const history = useHistory()
    const authenStore = useAuthenStore()
    const loadingHook = useLoading()
    const { searchDeletedProject } = useSearchDeletedProject()

    const [dataList, setDataList] = useState<DeletedProject[]>([])
    const [keywordSearch, setKeywordSearch] = useQueryParam<string | undefined>('keywordSearch')

    const setSearchParam = (value: DeletedProjectRequest) => {
        setKeywordSearch(value.keywordSearch)
    }

    const [initialValue, setInitialValue] = useState<DeletedProjectRequest>({
        keywordSearch: keywordSearch,
        userID: authenStore.profile.userId ?? 0,
    })

    useEffect(() => {
        if (authenStore.profile.userId) {
            onSubmit(initialValue)
        }
    }, [authenStore.profile.userId])

    const onSubmit = async (value: DeletedProjectRequest) => {
        loadingHook.loadingOpen()
        setInitialValue(value)
        const data = await searchDeletedProject({
            keywordSearch: value.keywordSearch,
            userID: authenStore.profile.userId ?? 0,
        })
        setDataList(data?.result?.items ?? [])
        setSearchParam(value)
        loadingHook.loadingClose()
    }

    const onClear = async (form: any) => {
        form.reset()
        setInitialValue({
            userID: authenStore.profile.userId ?? 0,
        })
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const onBack = () => {
        history.push('/dashboard')
    }

    const handleExportExcel = () => {
        if (!dataList.length) return
        deletedProjectExport({
            header: {
                keyword: keywordSearch ?? '-',
            },
            content: dataList.map((item) => ({
                projectID: item.projectNumber,
                deletedDate: convertDate(item.deletedDate),
                remarkDeleted: item.remarkDeleted ?? '-',
                researcherName: item.name?.[lang] ?? '-',
                projectNameTH: item.projectName?.['th'] ?? '-',
                projectNameEN: item.projectName?.['en'] ?? '-',
                projectType: item.considerTypeName?.[lang] ?? '-',
            })),
            translate: (value: string) => translate.get(value),
        })
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <SearchAccordion title={translate.get('UM.DELETED_PROJECT.HEADER')} isBack={true} onClickBack={onBack}>
                    <DeletedProjectPanel onSubmit={onSubmit} initialValue={initialValue} onClear={onClear} />
                </SearchAccordion>
                <Grid container justify="space-between" alignItems="flex-end" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList.length}`])}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={
                                        <GetAppRoundedIcon style={{ color: dataList.length ? '#DB5F8E' : '#e0e0e0' }} />
                                    }
                                    disabled={!dataList.length}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Table dataList={dataList} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    container: {
        margin: '15px 0px',
        width: '100%',
    },
    header: {
        marginBottom: '16px',
        fontWeight: 'bold',
    },
}))

export default DeletedProjectForm
